// framework/client/helpers/MessagingClient/MessageBubble.js
import { ComponentBase } from "../../../core/ComponentBase.js"; // Adjusted path
import { marked } from "https://cdn.jsdelivr.net/npm/marked/lib/marked.esm.js"; // For rendering markdown in messages

export class MessageBubble extends ComponentBase {
    static styles = `
        .message-bubble-wrapper {
            display: flex;
            margin-bottom: 0.5rem; /* Space between messages */
            max-width: 75%; /* Max width of a bubble */
        }
        .message-bubble-wrapper.own-message {
            justify-content: flex-end; /* Align own messages to the right */
            margin-left: auto; /* Push to the right */
        }
        .message-bubble-wrapper.other-message {
            justify-content: flex-start; /* Align others' messages to the left */
            margin-right: auto; /* Push to the left */
        }
        .message-bubble {
            padding: 0.5rem 0.75rem;
            border-radius: 1rem; /* More rounded bubbles */
            position: relative; /* For timestamp positioning */
            min-width: 50px; /* Minimum width */
        }
        .message-bubble.own-message {
            background-color: var(--bs-primary-bg-subtle); /* Theme primary subtle background */
            color: var(--bs-primary-text-emphasis);
            border-bottom-right-radius: 0.25rem; /* Tail for own messages */
        }
        .message-bubble.other-message {
            background-color: var(--bs-secondary-bg); /* Theme secondary background */
            color: var(--bs-secondary-text-emphasis);
            border-bottom-left-radius: 0.25rem; /* Tail for other messages */
        }
        .message-sender-name { /* For group chats */
            font-size: 0.75rem;
            font-weight: bold;
            color: var(--bs-emphasis-color);
            margin-bottom: 0.25rem;
            display: block; /* Ensure it takes its own line */
        }
        .message-content {
            word-wrap: break-word; /* Wrap long words */
            white-space: pre-wrap;  /* Preserve whitespace and newlines */
        }
        .message-content img, .message-content video { /* Basic styling for media */
            max-width: 100%;
            max-height: 200px; /* Limit preview height */
            border-radius: 0.5rem;
            margin-top: 0.5rem;
            display: block;
        }
        .message-content .attachment-link {
            display: block;
            margin-top: 0.5rem;
            font-size: 0.9rem;
        }
        .message-timestamp {
            font-size: 0.7rem;
            color: var(--bs-tertiary-color);
            text-align: right;
            display: block; /* Full width for alignment */
            margin-top: 0.25rem;
        }
        /* Ensure markdown content is styled reasonably */
        .message-content p:last-child { margin-bottom: 0; }
        .message-content ul, .message-content ol { padding-left: 1.2rem; margin-bottom: 0.5rem; }
        .message-content pre {
            background-color: var(--bs-tertiary-bg);
            padding: 0.5rem;
            border-radius: 0.25rem;
            overflow-x: auto;
            font-size: 0.85em;
        }
    `;

    constructor(config) { // Expects { appClient, messageData, isGroupChat, instanceId }
        super(config);
        this.messageData = this.config.messageData;
        this.isGroupChat = this.config.isGroupChat;
        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'message-bubble-wrapper';
        } else {
            this.container.innerHTML = ''; // Clear if re-rendering
        }

        const bubble = document.createElement('div');
        bubble.className = 'message-bubble';

        if (this.messageData.isOwnMessage) {
            this.container.classList.add('own-message');
            bubble.classList.add('own-message');
        } else {
            this.container.classList.add('other-message');
            bubble.classList.add('other-message');
        }

        // Sender Name (for group chats, if not own message)
        if (this.isGroupChat && !this.messageData.isOwnMessage && this.messageData.senderDisplayName) {
            const senderName = document.createElement('span');
            senderName.className = 'message-sender-name';
            senderName.textContent = this.messageData.senderDisplayName;
            bubble.appendChild(senderName);
        }

        // Message Content
        const contentDiv = document.createElement('div');
        contentDiv.className = 'message-content';

        if (this.messageData.messageType === 'text' && this.messageData.content) {
            // Render markdown content
            try {
                contentDiv.innerHTML = marked.parse(this.messageData.content);
            } catch (e) {
                this.log.error("Error parsing markdown for message content:", e);
                contentDiv.textContent = this.messageData.content; // Fallback to plain text
            }
        } else if (this.messageData.messageType === 'image' && this.messageData.attachments && this.messageData.attachments.length > 0) {
            // Basic image preview (first image)
            const attachment = this.messageData.attachments[0];
            // TODO: Construct URL properly for attachments (likely using UploadDownloadService download route/key)
            // For Phase 1, let's assume attachment.url is directly usable or a placeholder.
            const img = document.createElement('img');
            img.src = attachment.url || `/download/${attachment.fileId}`; // Placeholder
            img.alt = attachment.fileName || 'Image attachment';
            contentDiv.appendChild(img);
            if (this.messageData.content) { // Caption
                const caption = document.createElement('p');
                caption.textContent = this.messageData.content;
                contentDiv.appendChild(caption);
            }
        } else if (this.messageData.messageType === 'file' && this.messageData.attachments && this.messageData.attachments.length > 0) {
            const attachment = this.messageData.attachments[0];
            const link = document.createElement('a');
            link.href = attachment.url || `/download/${attachment.fileId}`; // Placeholder
            link.textContent = `📎 ${attachment.fileName || 'Attachment'}`;
            link.target = '_blank';
            link.className = 'attachment-link';
            contentDiv.appendChild(link);
             if (this.messageData.content) { // Caption
                const caption = document.createElement('p');
                caption.textContent = this.messageData.content;
                contentDiv.appendChild(caption);
            }
        } else if (this.messageData.messageType === 'system') {
            contentDiv.innerHTML = `<em class="text-muted">${this.messageData.content}</em>`;
        } else {
            contentDiv.textContent = this.messageData.content || ''; // Fallback for other types or empty content
        }
        bubble.appendChild(contentDiv);

        // Timestamp
        const timestamp = document.createElement('div');
        timestamp.className = 'message-timestamp';
        timestamp.textContent = new Date(this.messageData.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        bubble.appendChild(timestamp);

        this.container.appendChild(bubble);
        this.appClient.tx.applyTranslations(this.container);
        return this.container;
    }

    destroy() {
        super.destroy();
    }
}